import React, { useState } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ProductDetailAIChatModalProps {
  open: boolean;
  onClose: () => void;
  productPrice?: number;
}

export const ProductDetailAIChatModal = ({
  open,
  onClose,
  productPrice = 500000,
}: ProductDetailAIChatModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: `Hello! I'm your AI loan advisor. I can help you analyze loan programs, compare rates, and provide personalized recommendations for your $${productPrice.toLocaleString()} property. What would you like to know?`,
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText('');

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputText);
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMessage]);
    }, 1000);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    if (
      input.includes('conventional') ||
      input.includes('fha') ||
      input.includes('va')
    ) {
      return `Based on your property value of $${productPrice.toLocaleString()}, here are my recommendations:

• **Conventional Loan**: Best for borrowers with 20%+ down payment, lower PMI costs
• **FHA Loan**: Great for first-time buyers, lower credit score requirements (3.5% down)
• **VA Loan**: Excellent for veterans with 0% down payment and competitive rates

Would you like me to analyze specific loan programs for your situation?`;
    }

    if (input.includes('rate') || input.includes('interest')) {
      return `Current market rates are trending around 6.5-7.2% for conventional loans. However, rates vary based on:

• Credit score (aim for 740+ for best rates)
• Down payment amount
• Loan term (15 vs 30 years)
• Property type and location

I can help you calculate specific rates based on your profile. What's your credit score range?`;
    }

    if (input.includes('down payment') || input.includes('downpayment')) {
      return `For a $${productPrice.toLocaleString()} property, here are your down payment options:

• **3.5% down**: $${(productPrice * 0.035).toLocaleString()} (FHA)
• **5% down**: $${(productPrice * 0.05).toLocaleString()} (Conventional)
• **10% down**: $${(productPrice * 0.1).toLocaleString()} (Conventional)
• **20% down**: $${(productPrice * 0.2).toLocaleString()} (Conventional, no PMI)

What's your target down payment amount?`;
    }

    if (input.includes('monthly') || input.includes('payment')) {
      const monthlyPayment = (productPrice * 0.07) / 12; // Rough estimate
      return `For a $${productPrice.toLocaleString()} property with 20% down at 7% interest:

**Estimated Monthly Payment: $${monthlyPayment.toLocaleString()}**

This includes:
• Principal & Interest: ~$${(monthlyPayment * 0.8).toLocaleString()}
• Property Tax: ~$${(monthlyPayment * 0.15).toLocaleString()}
• Insurance: ~$${(monthlyPayment * 0.05).toLocaleString()}

Would you like me to calculate payments for different scenarios?`;
    }

    return `I understand you're asking about "${userInput}". I can help you with:

• Loan program comparisons
• Rate analysis and trends
• Down payment strategies
• Monthly payment calculations
• Credit score optimization
• Property investment analysis

What specific aspect would you like to explore further?`;
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, var(--surface) 0%, var(--surface-container-low) 100%)`,
          border: '2px solid rgba(4, 58, 147, 0.2)',
          borderRadius: isMobile ? 0 : 3,
          height: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '80vh',
          boxShadow: `
            0 25px 80px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: 'white',
          borderBottom: '1px solid rgba(4, 58, 147, 0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: isMobile ? 2 : 3,
          background: `linear-gradient(135deg, rgba(4, 58, 147, 0.2) 0%, rgba(125, 102, 217, 0.15) 100%)`,
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <SmartToyIcon sx={{ color: '#7d66d9' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
            AI Loan Advisor
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label="$50"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.2),
              color: 'white',
              fontSize: '0.75rem',
            }}
          />
          <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
            Premium Feature
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            p: isMobile ? 1.5 : 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            background: `linear-gradient(135deg, rgba(2, 5, 18, 0.3) 0%, rgba(7, 5, 14, 0.2) 100%)`,
            backdropFilter: 'blur(10px)',
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                mb: 1,
              }}
            >
              <Paper
                sx={{
                  maxWidth: isMobile ? '85%' : '70%',
                  p: isMobile ? 1.5 : 2,
                  background: message.isUser
                    ? `linear-gradient(135deg, rgba(4, 58, 147, 0.25) 0%, rgba(4, 58, 147, 0.15) 100%)`
                    : `linear-gradient(135deg, rgba(125, 102, 217, 0.2) 0%, rgba(125, 102, 217, 0.1) 100%)`,
                  border: `1px solid ${alpha(
                    message.isUser
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                    0.4
                  )}`,
                  borderRadius: 2,
                  position: 'relative',
                  backdropFilter: 'blur(20px)',
                  boxShadow: `
                    0 8px 32px rgba(0, 0, 0, 0.2),
                    inset 0 1px 0 rgba(255, 255, 255, 0.3)
                  `,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  {message.isUser ? (
                    <PersonIcon
                      sx={{ fontSize: isMobile ? 14 : 16, color: '#043a93' }}
                    />
                  ) : (
                    <SmartToyIcon
                      sx={{ fontSize: isMobile ? 14 : 16, color: '#7d66d9' }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="rgba(255, 255, 255, 0.8)"
                    sx={{ fontWeight: 600 }}
                  >
                    {message.isUser ? 'You' : 'AI Advisor'}
                  </Typography>
                </Box>
                <Typography
                  variant={isMobile ? 'body2' : 'body2'}
                  color="white"
                  sx={{
                    whiteSpace: 'pre-line',
                    lineHeight: 1.6,
                    fontSize: isMobile ? '0.875rem' : '0.95rem',
                  }}
                >
                  {message.text}
                </Typography>
                <Typography
                  variant="caption"
                  color="rgba(255, 255, 255, 0.6)"
                  sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }}
                >
                  {message.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: isMobile ? 1.5 : 2,
            borderTop: '1px solid rgba(4, 58, 147, 0.2)',
            display: 'flex',
            gap: 1,
            background: `linear-gradient(135deg, rgba(2, 5, 18, 0.9) 0%, rgba(7, 5, 14, 0.8) 100%)`,
            backdropFilter: 'blur(20px)',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={isMobile ? 2 : 3}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about loan programs, rates, payments, or anything else..."
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                background: `linear-gradient(135deg, rgba(2, 5, 18, 0.8) 0%, rgba(7, 5, 14, 0.7) 100%)`,
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(4, 58, 147, 0.3)',
                borderRadius: 2,
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(4, 58, 147, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7d66d9',
                  boxShadow: '0 0 0 2px rgba(125, 102, 217, 0.3)',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '0.875rem' : '1rem',
                padding: isMobile ? 1.5 : 2,
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(176, 176, 176, 0.7)',
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
            sx={{
              bgcolor: '#7d66d9',
              color: 'white',
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              '&:hover': {
                bgcolor: '#6b5bc7',
                transform: 'scale(1.05)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(176, 176, 176, 0.3)',
                color: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <SendIcon sx={{ fontSize: isMobile ? 18 : 20 }} />
          </IconButton>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 2,
          borderTop: '1px solid rgba(4, 58, 147, 0.2)',
          background: `linear-gradient(135deg, rgba(2, 5, 18, 0.9) 0%, rgba(7, 5, 14, 0.8) 100%)`,
          backdropFilter: 'blur(20px)',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 1,
        }}
      >
        <Button
          onClick={onClose}
          fullWidth={isMobile}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 2,
            '&:hover': {
              color: 'white',
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          fullWidth={isMobile}
          sx={{
            background: 'linear-gradient(135deg, #7d66d9 0%, #6b5bc7 100%)',
            color: 'white',
            borderRadius: 2,
            '&:hover': {
              background: 'linear-gradient(135deg, #6b5bc7 0%, #5a4bb5 100%)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Purchase Access ($50)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
